/**
 * Macroeconomic Impact Calculator for SAFECARE Act
 * Estimates stylized GDP and Unemployment effects based on literature-based elasticities.
 * 
 * NOTE: These are illustrative projections based on simplified drivers, 
 * not a full general equilibrium model.
 */

export function calculateAnnualGrowthBoost(yearMetrics, config) {
    if (!yearMetrics) return 0;

    // Coefficients (Same as calculateMacroImpacts)
    const GDP_COEFFS = {
        healthSpending: 0.15,
        householdBurden: 0.10,
        employerCost: 0.08,
        debt: 0.05
    };

    // Calculate Drivers (Deltas vs Baseline)
    // Note: Inputs are percentages (0.10 for 10%) or fraction?
    // Let's assume input metric objects have requisite data

    // 1. Health Spending Delta (% of GDP)
    // Negative delta (savings) -> Positive impact
    const deltaHS = (yearMetrics.safecareHealthShareGDP || 0) - (yearMetrics.baselineHealthShareGDP || 0);

    // 2. Debt Delta (% of GDP)
    const deltaDebt = (yearMetrics.safecareDebtToGDP || 0) - (yearMetrics.baselineDebtToGDP || 0);

    // 3. Employer Cost Delta (Approx % of GDP)
    const baselineEmployerCostGDP = 0.045; // 4.5% GDP
    const safecareEmployerCostGDP = (config.employerRate || 0.10) * 0.45; // Rate * PayrollShare
    const deltaEC = safecareEmployerCostGDP - baselineEmployerCostGDP;

    // Calculate Raw Impact
    // Multipliers are Elasticities: % GDP change per 1% change in driver
    let boost = (
        GDP_COEFFS.healthSpending * (-deltaHS) +
        GDP_COEFFS.householdBurden * (-deltaHS * 0.5) + // Proxy: HH burden tracks spending
        GDP_COEFFS.employerCost * (-deltaEC) +
        GDP_COEFFS.debt * (-deltaDebt)
    );

    // Scale Logic
    // The coefficients are for "Long Term Equilibrium". For annual step, we dampen.
    // However, if runScenario applies this to GROWTH RATE, it compounds.
    // If applying to GDP LEVEL, it needs to track cumulative.
    // The user asked for "Scientific adjustment of expected growth".
    // Let's implement this as an annual GROWTH RATE adder.
    // e.g. Impact of 1% GDP level boost implies ~0.1% extra growth/year for 10 years.

    // We will return the "Growth Rate Adder" for this specific year.
    // Heuristic: The calculated 'boost' is the structural level shift. 
    // We convert this to a growth impulse: LevelShift / 10 years.

    const annualGrowthImpulse = boost / 10.0;

    // Cap at reasonable limits (-0.5% to +0.5% per year)
    return Math.max(-0.005, Math.min(0.005, annualGrowthImpulse));
}

export function calculateMacroImpacts(yearlyResults, config) {
    // Coefficients for GDP Impact (Year 10 targets)
    // Target range: -0.3% (worst case) to +1.8% (best case)
    const GDP_COEFFS = {
        healthSpending: 0.15,    // Impact of reduced health share of GDP
        householdBurden: 0.10,   // Impact of reduced household burden
        employerCost: 0.08,      // Impact of reduced employer costs
        debt: 0.05               // Impact of reduced federal debt
    };

    // Coefficients for Unemployment Impact (Year 10 targets)
    // Target range: -0.3% (best) to +0.2% (worst)
    const UNEMPLOYMENT_COEFFS = {
        employerCost: 0.02,      // Lower costs -> higher labor demand (reduces unemployment)
        adminDisplacement: 0.025, // Job losses in admin/insurance (increases unemployment)
        transitionDampener: 0.5   // Effectiveness of transition support
    };

    // Calculate Transition Score (0 to 1)
    // Higher score = better mitigation of displacement unemployment
    const transitionScore = (
        (config.severanceMonths / 9) * 0.3 +
        (config.retrainingFundingLevel === 'high' ? 1.0 : config.retrainingFundingLevel === 'medium' ? 0.6 : 0.3) * 0.4 +
        (config.tuitionSupportCoverage) * 0.3
    );

    return yearlyResults.map((yearData, index) => {
        // Only calculate impacts for active years (after startYear)
        if (yearData.year < config.startYear) {
            return {
                year: yearData.year,
                gdpImpact: 0,
                unemploymentImpact: 0,
                safecareGDP: yearData.baselineHealthShareGDP ? (yearData.totalRevenue / yearData.baselineHealthShareGDP) : 0, // Approx
                safecareUnemploymentRate: 4.0 // Assume baseline 4.0%
            };
        }

        // 1. Calculate Drivers (Deltas vs Baseline)

        // Change in Health Spending % GDP (Negative is good)
        const deltaHS = (yearData.safecareHealthShareGDP || 0) - (yearData.baselineHealthShareGDP || 0);

        // Change in Federal Debt % GDP (Negative is good)
        // Note: yearlyResults might calculate this cumulatively, we want the level diff
        const deltaDebt = (yearData.safecareDebtToGDP || 0) - (yearData.baselineDebtToGDP || 0);

        // Change in Employer Costs (Approximate based on rate vs baseline premiums)
        // Baseline employer share ~5% of GDP. SAFECARE rate ~8-10% of Payroll.
        // Simplified: Driver is the relative change in employer burden
        // If employerRate is low (8%), delta is negative (good). If high (12%), delta is positive (bad).
        const baselineEmployerCostGDP = 0.045; // Approx 4.5% of GDP is employer premiums
        const safecareEmployerCostGDP = config.employerRate * 0.45; // Approx 45% of GDP is payroll
        const deltaEC = safecareEmployerCostGDP - baselineEmployerCostGDP;

        // Change in Admin Spending (Proxy for displacement)
        // Negative deltaADM means admin jobs lost -> potential unemployment
        const deltaADM = (yearData.safecareAdminSpending || 0) - (yearData.baselineAdminSpending || 0);
        // Normalize as % of GDP for consistent scaling
        const deltaADM_GDP = deltaADM / 28e12; // Approx current GDP

        // 2. Calculate GDP Impact
        // Formula: GDP_Impact = sum(drivers * coeffs) * ramp
        // We use a ramp to simulate gradual economic adjustment
        const yearsActive = yearData.year - config.startYear + 1;
        const ramp = Math.min(1.0, yearsActive / 10); // 10-year full consistency

        let gdpImpact = (
            GDP_COEFFS.healthSpending * (-deltaHS) +
            // Household burden roughly tracks health spending saving, simplify to leverage HS
            GDP_COEFFS.householdBurden * (-deltaHS * 0.5) +
            GDP_COEFFS.employerCost * (-deltaEC) +
            GDP_COEFFS.debt * (-deltaDebt)
        );

        // Scale to realistic range (-0.5% to +2.0%)
        gdpImpact = gdpImpact * 8.0 * ramp;

        // 3. Calculate Unemployment Impact
        // Formula: U_Impact = (LaborDemand - Displacement) * ramp

        // Labor Demand Effect: Lower employer costs -> Lower U
        const laborDemandEffect = deltaEC * UNEMPLOYMENT_COEFFS.employerCost * 100; // Scale to % points

        // Displacement Effect: Admin cuts -> Higher U, damped by transition
        // deltaADM is usually negative (savings). So (-deltaADM) is positive (job loss).
        const rawDisplacement = (-deltaADM_GDP) * UNEMPLOYMENT_COEFFS.adminDisplacement * 1000;
        const netDisplacement = Math.max(0, rawDisplacement * (1 - transitionScore * UNEMPLOYMENT_COEFFS.transitionDampener));

        // Combined Net Impact
        // Transition spike fades over time (displacement effect reduces after 5 years)
        const transitionFade = yearsActive > 5 ? Math.max(0, 1 - (yearsActive - 5) / 5) : 1.0;

        let unemploymentImpact = (laborDemandEffect + (netDisplacement * transitionFade));

        // Clamp to realistic range (-0.5% to +0.5%)
        unemploymentImpact = Math.max(-0.5, Math.min(0.5, unemploymentImpact));

        return {
            year: yearData.year,
            gdpImpact: gdpImpact, // e.g., 0.015 for +1.5%
            unemploymentImpact: unemploymentImpact, // e.g. -0.2 for -0.2pp
            safecareGDP: 0, // calculated downstream or placeholder
            safecareUnemploymentRate: 4.0 + unemploymentImpact // Baseline fixed at 4.0%
        };
    });
}
